import { motion, useInView } from 'framer-motion';
import { useRef, ReactNode } from 'react';

interface ScrollRevealProps {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
  stagger?: boolean;
  staggerDelay?: number;
  threshold?: number;
}

const directionMap = {
  up: { y: 40, x: 0, opacity: 0 },
  down: { y: -40, x: 0, opacity: 0 },
  left: { x: 40, y: 0, opacity: 0 },
  right: { x: -40, y: 0, opacity: 0 },
  none: { y: 0, x: 0, opacity: 0 },
};

export default function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.7,
  className = '',
  once = true,
  stagger = false,
  staggerDelay = 0.1,
  threshold = 0.15,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once, margin: '-50px 0px -50px 0px', amount: threshold });

  const initial = directionMap[direction];

  if (stagger) {
    return (
      <div ref={ref} className={className}>
        {Array.isArray(children) &&
          children.map((child, i) => (
            <motion.div
              key={i}
              initial={{ y: 40, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : { y: 40, opacity: 0 }}
              transition={{
                duration,
                delay: delay + i * staggerDelay,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {child}
            </motion.div>
          ))}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={isInView ? { y: 0, x: 0, opacity: 1 } : initial}
      transition={{
        duration,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function ScrollStaggerContainer({
  children,
  className = '',
  delay = 0,
  staggerDelay = 0.1,
  threshold = 0.15,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  staggerDelay?: number;
  threshold?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px 0px -50px 0px', amount: threshold });

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children) &&
        children.map((child, i) => (
          <motion.div
            key={i}
            initial={{ y: 40, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : { y: 40, opacity: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * staggerDelay,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {child}
          </motion.div>
        ))}
    </div>
  );
}